import './control.css';
import './challenger.css';
import liveRoot from '../../01_SOURCE_AUDIT/live-root.html?raw';
import challengerArtifact from '../copy-job-credibility-first-v4-2026-08-26/final-copy.md?raw';
import { faqs } from './content.js';
import { checkoutUrl, handleCheckout, initTracking, event, routeContext } from './tracking.js';

const root = document.querySelector('#root');
const testimonialAvatars = new Map([
  ['Elena R., Financial Controller', '/assets/testimonial-elena.svg'],
  ['Mike, Military & Law Enforcement', '/assets/testimonial-mike.svg'],
  ['Roger H., Disabled Veteran', '/assets/testimonial-roger.svg'],
  ['Ms. A.G., Military Family & Martial Artist', '/assets/testimonial-ag.svg'],
  ['Jason H., Centerline Tactical Student', '/assets/testimonial-jason.svg'],
]);
const template = document.createElement('template');
template.innerHTML = liveRoot;
const optimizedImages = new Map([
  ['/assets/confrontation-scenario-DZ0HhrUg.png', '/assets/confrontation.webp'],
  ['/assets/course-materials-B8pvrgdA.png', '/assets/course-materials.webp'],
]);
template.content.querySelectorAll('img').forEach((image) => {
  const testimonialReplacement = testimonialAvatars.get(image.alt);
  const optimizedReplacement = optimizedImages.get(new URL(image.src, window.location.origin).pathname);
  if (testimonialReplacement) image.src = testimonialReplacement;
  else if (optimizedReplacement) image.src = optimizedReplacement;
});
root.replaceChildren(template.content);

const escapeHtml = (value) => value
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

function inlineMarkdown(value) {
  return escapeHtml(value)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
}

function renderBlocks(value) {
  return value.split(/\n\s*\n/).map((block) => {
    const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
    if (!lines.length) return '';
    if (lines.every((line) => line.startsWith('- '))) {
      return `<ul class="challenger-list">${lines.map((line) => `<li>${inlineMarkdown(line.slice(2))}</li>`).join('')}</ul>`;
    }
    const plain = lines.join(' ');
    const cta = plain.match(/^\*\*(Get DIFFUSE And Learn The CRISIS Sequence)\*\*$/);
    if (cta) return `<a class="cta-button longform-cta" href="#" data-cta="offer-bridge">${escapeHtml(cta[1])}</a>
      <div class="longform-trust" data-visual-only><span>$37, paid once</span><span>Lifetime access</span><span>60 days</span></div>`;
    if (/^[“"]/.test(plain) && /[”"]$/.test(plain)) return `<blockquote class="longform-quote">${inlineMarkdown(plain)}</blockquote>`;
    return `<p class="body-text">${lines.map(inlineMarkdown).join('<br>')}</p>`;
  }).join('');
}

function slugify(value) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function section(id, tone, inner, extraClass = '') {
  const node = document.createElement('section');
  node.dataset.section = id;
  node.className = `${tone} section-padding challenger-section longform-section ${extraClass}`.trim();
  node.innerHTML = `<div class="content-width longform-width">${inner}</div>`;
  return node;
}

function parseArtifact() {
  const match = challengerArtifact.match(/<!-- COPY:body START -->\s*([\s\S]*?)\s*<!-- COPY:body END -->/);
  if (!match) throw new Error('Reviewed Challenger copy markers are missing');
  const lines = match[1].trim().split('\n');
  const titleLine = lines.shift();
  if (!titleLine?.startsWith('# ')) throw new Error('Reviewed Challenger H1 is missing');
  const title = titleLine.slice(2).trim();
  while (!lines[0]?.trim()) lines.shift();
  const subheadLine = lines.shift();
  if (!subheadLine?.startsWith('## ')) throw new Error('Reviewed Challenger subhead is missing');
  const subhead = subheadLine.slice(3).trim();
  const sections = [];
  let current = { heading: 'Opening', lines: [] };
  lines.forEach((line) => {
    if (line.startsWith('## ')) {
      sections.push({ ...current, body: current.lines.join('\n').trim() });
      current = { heading: line.slice(3).trim(), lines: [] };
    } else {
      current.lines.push(line);
    }
  });
  sections.push({ ...current, body: current.lines.join('\n').trim() });
  return { title, subhead, opening: sections.shift()?.body || '', sections };
}

function buildChallenger() {
  const copy = parseArtifact();
  const main = document.createElement('main');
  const hero = section('hero', 'section-dark', `
    <div class="longform-hero__grid">
      <div class="longform-hero__title">
        <p class="longform-brand" data-visual-only>Centerline Tactical</p>
        <h1 class="headline everyday-hero__headline">${inlineMarkdown(copy.title)}</h1>
        <h2 class="everyday-hero__subhead">${inlineMarkdown(copy.subhead)}</h2>
      </div>
      <figure class="everyday-hero__visual">
        <img src="/assets/confrontation.webp" alt="A tense confrontation in an everyday parking lot" width="1200" height="900" fetchpriority="high">
      </figure>
      <div class="longform-prose longform-hero__lead">${renderBlocks(copy.opening)}</div>
    </div>`);
  main.append(hero);

  copy.sections.forEach((item, index) => {
    const id = slugify(item.heading);
    const isOffer = /what you get for \$37/i.test(item.heading);
    const isClose = /learn the order before/i.test(item.heading);
    const isMechanism = /hard boundary comes fourth/i.test(item.heading);
    const isAuthority = /phone call did not end/i.test(item.heading);
    const tone = isOffer ? 'section-light' : (index % 2 ? 'section-light' : 'section-dark');
    const textClass = tone === 'section-light' ? ' text-light-fg' : '';
    const offerImage = isOffer
      ? '<img class="challenger-offer__visual" src="/assets/course-materials.webp" alt="DIFFUSE lessons shown across laptop, tablet, and phone" width="1200" height="896">'
      : '';
    const offerCta = isOffer
      ? `<a class="cta-button longform-cta longform-cta--offer" href="#" data-cta="offer-bridge" data-repeat>Get DIFFUSE And Learn The CRISIS Sequence</a>
         <div class="longform-trust" data-visual-only><span>$37, paid once</span><span>Lifetime access</span><span>60 days</span></div>`
      : '';
    const mechanismDiagram = isMechanism
      ? `<div class="crisis-order" data-visual-only aria-label="CRISIS six-step sequence with Set a Limit at step four">
          ${[1, 2, 3, 4, 5, 6].map((step) => `<span class="crisis-order__step${step === 4 ? ' is-limit' : ''}"><b>${step}</b>${step === 4 ? '<small>Set a Limit</small>' : ''}</span>`).join('')}
        </div>`
      : '';
    const authorityImage = isAuthority
      ? '<figure class="longform-authority"><img src="/assets/adam-instructor.jpg" alt="Adam Seegmiller teaching DIFFUSE" width="1200" height="900"></figure>'
      : '';
    const inner = `
      <article class="longform-article${isOffer ? ' longform-offer' : ''}${isClose ? ' longform-close' : ''}">
        <h2 class="headline longform-heading${textClass}">${inlineMarkdown(item.heading)}</h2>
        <div class="longform-prose${textClass}">${renderBlocks(item.body)}</div>
        ${mechanismDiagram}
        ${authorityImage}
        ${offerCta}
        ${offerImage}
      </article>`;
    main.append(section(id, tone, inner, isOffer ? 'longform-offer-section' : ''));
  });
  return main;
}

if (routeContext().variant === 'challenger') {
  document.body.classList.add('challenger', 'everyday-conflict', 'longform-challenger');
  const shell = root.querySelector('.min-h-screen');
  const footer = shell.querySelector('footer');
  shell.querySelectorAll(':scope > section[data-section], :scope > hr.section-divider').forEach((node) => node.remove());
  shell.insertBefore(buildChallenger(), footer);
}

const checkoutLocations = new Map([
  ['Arm Me With DIFFUSE', 'offer'],
  ['I Want The Control', 'transformation'],
  ['Give Me DIFFUSE', 'final'],
  ['Get DIFFUSE And Learn The CRISIS Sequence', 'offer-bridge'],
]);

root.querySelectorAll('a[data-jump]').forEach((link) => {
  link.addEventListener('click', () => event('page_jump', { target_section: link.dataset.jump }));
});

root.querySelectorAll('a[data-cta]').forEach((link) => {
  const label = link.textContent.trim();
  const directLocation = link.dataset.cta;
  const checkoutIds = ['offer-bridge', 'offer', 'transformation', 'final'];
  const location = checkoutIds.includes(directLocation) ? directLocation : checkoutLocations.get(label);
  if (!location) {
    const target = link.getAttribute('href')?.replace(/^#/, '') || 'buy-box';
    link.dataset.jump = target;
    return;
  }
  link.dataset.cta = location;
  link.href = checkoutUrl(location);
  link.addEventListener('click', (clickEvent) => handleCheckout(link, clickEvent));
});

root.querySelectorAll('[data-section="faq"] button').forEach((button, index) => {
  const container = button.parentElement;
  const glyph = button.lastElementChild;
  const answerId = `faq-answer-${index + 1}`;
  button.type = 'button';
  button.dataset.faq = `faq-${index + 1}`;
  button.setAttribute('aria-expanded', 'false');
  button.setAttribute('aria-controls', answerId);
  const answer = document.createElement('p');
  answer.id = answerId;
  answer.className = 'body-text mt-4 text-muted-light';
  answer.setAttribute('role', 'region');
  answer.setAttribute('aria-labelledby', button.id || `faq-question-${index + 1}`);
  if (!button.id) button.id = `faq-question-${index + 1}`;
  answer.textContent = faqs[index][1];
  answer.hidden = true;
  container.append(answer);
  button.addEventListener('click', () => {
    const open = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!open));
    glyph.textContent = open ? '+' : '−';
    answer.hidden = open;
    if (!open) event('faq_open', { faq_id: button.dataset.faq });
  });
});

initTracking();
